import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Tabs, Tab, TextField, Select, MenuItem, Button, CircularProgress } from '@mui/material';
import { Close as CloseIcon, AutoAwesome as AIIcon, Search as SearchIcon, Save as SaveIcon, KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { runAIVariables, saveAIVariableKeys, getAIVariableKeyStatus } from '../../../store/Campaign';
import { getProperties } from '../../../store/Objects';

// ─── Constants ────────────────────────────────────────────────────────────────

const MODELS = [
  { id: 'gpt-4o',                   label: 'GPT-4o',                          credits: 1, dot: '#10B981' },
  { id: 'gpt-4o-mini',              label: 'GPT-4o Mini',                     credits: 1, dot: '#10B981' },
  { id: 'claude-3.7-sonnet-online', label: 'Claude 3.7 Sonnet (Web Search)',  credits: 3, dot: '#8B5CF6' },
  { id: 'claude-sonnet-4.6',        label: 'Claude Sonnet 4.6',               credits: 2, dot: '#7C3AED' },
  { id: 'claude-sonnet-4-online',   label: 'Claude Sonnet 4 (Web Search)',    credits: 3, dot: '#7C3AED' },
];


const TEMPLATES = [
  {
    title: 'Icebreaker', columnName: 'icebreaker', model: 'gpt-4o', credits: 2, category: 'Copywriting', description: 'A unique one-liner referencing their LinkedIn profile, role and company.', prompt: `You are a B2B cold email expert. Visit the LinkedIn profile at {{linkedin_url}} and write a single, natural icebreaker sentence (max 20 words) for {{first_name}} who is a {{job_title}} at {{company_name}}.\n\nRules:\n- Reference something specific from their LinkedIn profile (recent post, achievement, headline).\n- If the LinkedIn URL is unavailable, fall back to their role and company.\n- Sound human, not robotic.\n- Do NOT start with "I" or "I noticed".\n- Output ONLY the sentence. No quotes, no explanation.`,
  },
  {
    title: 'Opening Line', columnName: 'opening_line', model: 'gpt-4o', credits: 2, category: 'Copywriting', description: 'A tailored first line referencing something real from their company website.', prompt: `You are a B2B cold email expert. Visit {{website}} and write a single opening line (max 25 words) for a cold email to {{first_name}} at {{company_name}} in the {{industry}} industry.\n\nRules:\n- Reference something specific from their website (tagline, service, mission).\n- If the website is unavailable, use their company name and industry.\n- Sound like a real person wrote it.\n- Do NOT say "I hope this email finds you well".\n- Do NOT pitch anything.\n- Output ONLY the line. No quotes, no explanation.`,
  },
  {
    title: 'Subject Line', columnName: 'subject_line', model: 'gpt-4o-mini', credits: 1, category: 'Copywriting', description: 'A crisp, high-open-rate subject line based on the lead\'s name and company.', prompt: `Write one cold email subject line for {{first_name}} at {{company_name}}.\n\nRules:\n- Max 7 words.\n- Feel specific to them, not mass-blasted.\n- No exclamation marks, no all-caps, no spam words.\n- Output ONLY the subject line. No quotes, no explanation.`,
  },
  {
    title: 'Compliment', columnName: 'compliment', model: 'gpt-4o', credits: 2, category: 'Copywriting', description: 'A genuine company compliment based on what\'s actually on their website.', prompt: `You are a B2B cold email expert. Visit {{website}} and write a single compliment sentence (max 18 words) about {{company_name}} that feels genuine and specific.\n\nRules:\n- Reference something real from their website (a service, mission, or unique offer).\n- If the website is unavailable, base it on their industry: {{industry}} and size: {{company_size}}.\n- Avoid clichés like "amazing team" or "great company".\n- Output ONLY the compliment. No quotes, no explanation.`,
  },
  {
    title: 'Pain Point', columnName: 'pain_point', model: 'gpt-4o', credits: 2, category: 'Research', description: 'A one-liner that calls out a likely frustration based on their role and LinkedIn.', prompt: `You are a B2B cold email expert. Visit {{linkedin_url}} and write one sentence (max 20 words) describing a real frustration that {{first_name}} likely faces as a {{job_title}} at a {{industry}} company.\n\nRules:\n- Use their LinkedIn headline or summary to make it more specific if available.\n- If LinkedIn is unavailable, use their role and industry.\n- Sound like a relatable observation, not a sales pitch.\n- Do NOT mention any product or solution.\n- Output ONLY the sentence. No quotes, no explanation.`,
  },
  {
    title: 'Call to Action', columnName: 'cta_line', model: 'gpt-4o-mini', credits: 1, category: 'Copywriting', description: 'A soft, low-friction closing line to ask for a quick call.', prompt: `Write a single closing CTA line (max 15 words) for a cold email to {{first_name}}. Ask if they're open to a quick 15-minute conversation.\n\nRules:\n- Sound casual, not pushy.\n- Do NOT say "hop on a call", "pick your brain", or "circle back".\n- Output ONLY the line. No quotes, no explanation.`,
  },
  {
    title: 'Follow-Up Line', columnName: 'followup_line', model: 'gpt-4o-mini', credits: 1, category: 'Copywriting', description: 'A re-engagement line for a lead who hasn\'t replied yet.', prompt: `Write a single casual follow-up sentence (max 15 words) to {{first_name}} at {{company_name}} who never replied to a previous email.\n\nRules:\n- Sound human and light, not desperate.\n- Do NOT say "just following up", "bumping this", or "circling back".\n- Output ONLY the sentence. No quotes, no explanation.`,
  },
  {
    title: 'Location Line', columnName: 'location_line', model: 'gpt-4o-mini', credits: 1, category: 'Copywriting', description: 'A natural city/state reference to make the email feel local.', prompt: `Write a short phrase (max 12 words) that naturally references {{city}}, {{state}} in a cold email to a {{industry}} professional.\n\nExample output: "as one of the growing agencies in Austin"\n\nRules:\n- Sound natural, not forced.\n- Output ONLY the phrase. No quotes, no punctuation at end.`,
  },
  {
    title: 'LinkedIn Note', columnName: 'linkedin_note', model: 'gpt-4o', credits: 2, category: 'Copywriting', description: 'A short connection request based on their actual LinkedIn profile.', prompt: `You are a B2B sales professional. Visit the LinkedIn profile at {{linkedin_url}} and write a connection request note to {{first_name}}, a {{job_title}} at {{company_name}}.\n\nRules:\n- Max 280 characters.\n- Reference something specific from their LinkedIn profile if available (a post, achievement, or their headline).\n- If LinkedIn is unavailable, reference their role and company.\n- Do NOT pitch anything.\n- Do NOT say "I'd love to connect" or "I came across your profile".\n- Output ONLY the note. No quotes, no explanation.`,
  },
  {
    title: 'PS Line', columnName: 'ps_line', model: 'gpt-4o-mini', credits: 1, category: 'Copywriting', description: 'A punchy closing P.S. that adds a personal touch to the email.', prompt: `Write a P.S. line (max 15 words) for a cold email to {{first_name}} at {{company_name}} in the {{industry}} industry.\n\nRules:\n- Start with "P.S."\n- Sound personal and conversational.\n- Do NOT repeat the main CTA.\n- Output ONLY the P.S. line. No explanation.`,
  },
];

const CATEGORY_COLORS = {
  Copywriting: { bg: '#EDE9FE', color: '#7C3AED' },
  Research:    { bg: '#DBEAFE', color: '#1D4ED8' },
};

function getInitials(contact) {
  const f = contact?.firstName?.[0] || '';
  const l = contact?.lastName?.[0] || '';
  return (f + l).toUpperCase() || contact?.email?.[0]?.toUpperCase() || '?';
}

const AVATAR_COLORS = ['#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'];
const MASKED_SENTINEL = '••••••••••••••••';

// ─── Component ────────────────────────────────────────────────────────────────

const AIVariablesDrawer = ({ open, onClose, contacts = [], totalContactsCount, filters, onColumnGenerated, campaignId }) => {
  const dispatch = useDispatch();
  const { aiVariablesLoading, aiVariablesColumns, aiVariableKeyStatus } = useSelector(s => s['Campaign']);
  const creditsBalance = Math.floor(useSelector(s => s['Settings']?.['currentSubscription']?.['creditsBalance']) || 0);
  const contactProperties = useSelector(s => s['Objects']?.['contacts']?.['properties'] || {});

  const [tab, setTab] = useState(0);
  const [columnName, setColumnName] = useState('');
  const [editingColumn, setEditingColumn] = useState(false);
  const [model, setModel] = useState('gpt-4o');
  const [prompt, setPrompt] = useState('');
  const [showVars, setShowVars] = useState(false);
  const [previewContact, setPreviewContact] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [apiKeys, setApiKeys] = useState({
    openai:    aiVariableKeyStatus.openai    ? MASKED_SENTINEL : '',
    anthropic: aiVariableKeyStatus.anthropic ? MASKED_SENTINEL : '',
  });
  const [savingKeys, setSavingKeys] = useState(false);
  const [showRunMenu, setShowRunMenu] = useState(false);
  const [enrichFirst, setEnrichFirst] = useState(1);
  const [varDropdown, setVarDropdown] = useState({ show: false, query: '', top: 0, left: 0, triggerIndex: -1 });
  const [varDropdownIndex, setVarDropdownIndex] = useState(0);
  const [promptFocused, setPromptFocused] = useState(false);
  const promptRef = useRef(null);
  const promptEditRef = useRef(null);
  const previewContacts = contacts.slice(0, 5);
  const totalContacts = totalContactsCount ?? contacts.length;
  const selectedModel = MODELS.find(m => m.id === model) || MODELS[0];
  const creditEstimate = selectedModel.credits * totalContacts;
  const usingOwnKey = (model.includes('gpt') && aiVariableKeyStatus.openai) || (model.includes('claude') && aiVariableKeyStatus.anthropic);
  const dynamicVariables = Object.values(contactProperties).flat().filter(p => p?.name && !['tags', 'status', 'source', 'workspace', 'contact_owner', 'updatedBy', 'workspaceId', 'updatedAt', 'createdAt', 'importJobId', 'ownerId', 'createdBy'].includes(p.name)).map(p => `{{${p.name}}}`);
  const filteredVarDropdown = varDropdown.query ? dynamicVariables.filter(v => v.toLowerCase().includes(varDropdown.query.toLowerCase())) : dynamicVariables;

  const resolveVarValue = (key) => {
    if (!previewContact) return null;
    // Try key as-is, then snake_case → camelCase conversion, then customFields
    const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    const val = previewContact[key] ?? previewContact[camel] ?? previewContact.customFields?.[key] ?? previewContact.customFields?.[camel];
    return (val !== undefined && val !== null && val !== '') ? String(val) : null;
  };

  const renderPromptAsHtml = (text) =>
    text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      const value = resolveVarValue(key);
      return value
        ? `<span class="aiv-prompt-chip aiv-prompt-chip--resolved" contenteditable="false">${value}</span>`
        : `<span class="aiv-prompt-chip" contenteditable="false">{{${key}}}</span>`;
    }).replace(/\n/g, '<br/>');

  useEffect(() => {
    if (open) {
      dispatch(getAIVariableKeyStatus());
      dispatch(getProperties());
      if (previewContacts.length > 0 && !previewContact) setPreviewContact(previewContacts[0]);
    }
  }, [open]);

  // Sync masked dots whenever key status arrives from server
  useEffect(() => {
    setApiKeys(k => ({
      openai:    aiVariableKeyStatus.openai    ? MASKED_SENTINEL : (k.openai    === MASKED_SENTINEL ? '' : k.openai),
      anthropic: aiVariableKeyStatus.anthropic ? MASKED_SENTINEL : (k.anthropic === MASKED_SENTINEL ? '' : k.anthropic),
    }));
  }, [aiVariableKeyStatus]);

  useEffect(() => {
    if (previewContacts.length > 0 && !previewContact) setPreviewContact(previewContacts[0]);
  }, [contacts]);

  // Sync preview text when column result comes in for selected contact
  useEffect(() => {
    if (columnName && aiVariablesColumns[columnName] && previewContact) {
      const value = aiVariablesColumns[columnName][previewContact._id];
      if (value !== undefined) setPreviewText(value);
    }
  }, [aiVariablesColumns, columnName, previewContact]);

  const getPlainTextFromEditor = () => {
    const el = promptEditRef.current;
    if (!el) return prompt;
    let text = '';
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) text += node.textContent;
      else if (node.nodeName === 'BR') text += '\n';
      else if (node.dataset?.var) text += `{{${node.dataset.var}}}`;
      else text += node.textContent;
    });
    return text;
  };

  const insertVariable = (v) => {
    if (promptFocused && promptEditRef.current) {
      promptEditRef.current.focus();
      document.execCommand('insertText', false, v);
      setPrompt(getPlainTextFromEditor());
    } else setPrompt(p => p + v);
    setShowVars(false);
  };

  const insertVarFromDropdown = (variable) => {
    const ta = promptRef.current?.querySelector('textarea');
    if (!ta) return;
    const cursorPos = ta.selectionStart;
    const before = prompt.slice(0, varDropdown.triggerIndex);
    const after = prompt.slice(cursorPos);
    const newVal = before + variable + after;
    setPrompt(newVal);
    setVarDropdown(d => ({ ...d, show: false }));
    setVarDropdownIndex(0);
    setTimeout(() => {
      const newPos = before.length + variable.length;
      ta.setSelectionRange(newPos, newPos);
      ta.focus();
    }, 0);
  };

  const handlePromptChange = (e) => {
    const val = e.target.value;
    setPrompt(val);
    const cursor = e.target.selectionStart;
    const textUpToCursor = val.slice(0, cursor);
    const match = textUpToCursor.match(/\{+(\w*)$/);
    if (match) {
      setVarDropdown({ show: true, query: match[1] || '', top: 0, left: 0, triggerIndex: match.index });
      setVarDropdownIndex(0);
    } else setVarDropdown(d => ({ ...d, show: false }));
  };

  const handlePromptKeyDown = (e) => {
    if (!varDropdown.show || filteredVarDropdown.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setVarDropdownIndex(i => Math.min(i + 1, filteredVarDropdown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setVarDropdownIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertVarFromDropdown(filteredVarDropdown[varDropdownIndex]); }
    else if (e.key === 'Escape') { setVarDropdown(d => ({ ...d, show: false })); }
  };

  const handleTestPreview = () => {
    if (!prompt || !previewContact) return;
    dispatch(runAIVariables({ contacts: [previewContact], prompt, model, columnName: '__preview__', campaignId,
      successCallback: (body) => {
        const val = body.results?.[0]?.value || '';
        setPreviewText(val);
      },
    }));
  };

  const handleRunAll = () => {
    if (!prompt || !columnName) return;
    const creditsNeeded = selectedModel.credits * totalContacts;
    if (creditsBalance < creditsNeeded && !usingOwnKey) {
      const maxContacts = Math.floor(creditsBalance / selectedModel.credits);
      toast.error(`Not enough credits. Need ${creditsNeeded}, have ${creditsBalance}. Select ${maxContacts} or fewer contacts.`);
      return;
    }
    dispatch(runAIVariables({ contacts, filters, useAllContacts: true, prompt, model, columnName, campaignId,
      successCallback: (body) => {
        if (onColumnGenerated) onColumnGenerated({ columnName, results: body.results });
      },
    }));
  };

  const handleRunN = (n) => {
    setShowRunMenu(false);
    if (!prompt) { toast.error('Please enter a prompt first.'); return; }
    if (!columnName) { toast.error('Please enter an Output Column name first.'); return; }
    const isAll = n === 'all';
    const count = isAll ? totalContacts : Math.min(n, totalContacts);
    const creditsNeeded = selectedModel.credits * count;
    if (creditsBalance < creditsNeeded && !usingOwnKey) {
      const maxContacts = Math.floor(creditsBalance / selectedModel.credits);
      toast.error(`Not enough credits. Need ${creditsNeeded}, have ${creditsBalance}. Select ${maxContacts} or fewer contacts.`);
      return;
    }
    dispatch(runAIVariables({ contacts: contacts.slice(0, Math.min(n, contacts.length)), filters, limit: isAll ? undefined : count, useAllContacts: isAll, prompt, model, columnName, campaignId,
      successCallback: (body) => {
        if (onColumnGenerated) onColumnGenerated({ columnName, results: body.results });
      },
    }));
  };

  const handleSaveKeys = () => {
    setSavingKeys(true);
    const keysToSave = {
      openai:    apiKeys.openai    === MASKED_SENTINEL ? '' : apiKeys.openai,
      anthropic: apiKeys.anthropic === MASKED_SENTINEL ? '' : apiKeys.anthropic,
    };
    dispatch(saveAIVariableKeys({ ...keysToSave, successCallback: () => setSavingKeys(false),
      failCallback: () => setSavingKeys(false),
    }));
  };

  const handleUseTemplate = (t) => {
    setPrompt(t.prompt);
    setColumnName(t.columnName || t.title);
    if (t.model) setModel(t.model);
    setEditingColumn(false);
    setTab(0);
  };

  const filteredTemplates = TEMPLATES.filter(t =>
    t.title.toLowerCase().includes(templateSearch.toLowerCase()) ||
    t.description.toLowerCase().includes(templateSearch.toLowerCase())
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose} className="aiv-drawer">
      <div className="aiv-drawer-content">

        {/* Header */}
        <div className="aiv-header">
          <div className="aiv-header-title">
            <div className="aiv-header-icon">
              <AIIcon style={{ fontSize: 18, color: 'var(--primary-color)' }} />
            </div>
            <div>
              <div className="aiv-title">AI Variables</div>
              <div className="aiv-subtitle">Enrich leads with AI-generated columns</div>
            </div>
          </div>
          <button className="aiv-close" onClick={onClose}><CloseIcon style={{ fontSize: 18 }} /></button>
        </div>

        {/* Tabs */}
        <div className="aiv-tabs">
          <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === 2) dispatch(getAIVariableKeyStatus()); }} className="aiv-tabs-inner">
            <Tab label="Build Prompt" disableRipple />
            <Tab label="Templates" disableRipple />
            <Tab label="Settings" disableRipple />
          </Tabs>
        </div>

        {/* Body */}
        <div className="aiv-body">

          {/* ── Tab 0: Build Prompt ── */}
          {tab === 0 && (
            <div className="aiv-build">

              {/* Output Column */}
              <div className="aiv-field">
                <label className="aiv-label">Output Column <span className="aiv-required">*</span></label>
                {editingColumn ? (
                  <div className="aiv-column-input-row">
                    <TextField
                      size="small" fullWidth autoFocus
                      value={columnName}
                      onChange={e => setColumnName(e.target.value)}
                      placeholder="e.g. icebreaker"
                      onKeyDown={e => { if (e.key === 'Enter') setEditingColumn(false); if (e.key === 'Escape') setEditingColumn(false); }}
                    />
                    <Button size="small" variant="contained" onClick={() => setEditingColumn(false)} disabled={!columnName}>Save</Button>
                    <button className="aiv-column-cancel" onClick={() => { setEditingColumn(false); setColumnName(''); }}><CloseIcon style={{ fontSize: 14 }} /></button>
                  </div>
                ) : (
                  <button className="aiv-column-placeholder" onClick={() => setEditingColumn(true)}>
                    {columnName ? <><span className="aiv-column-name">{columnName}</span></> : <>+ Create New Column</>}
                  </button>
                )}
              </div>

              {/* AI Model */}
              <div className="aiv-field">
                <label className="aiv-label">AI Model <span className="aiv-required">*</span></label>
                <Select value={model} onChange={e => setModel(e.target.value)} size="small" fullWidth className="aiv-model-select">
                  {MODELS.map(m => (
                    <MenuItem key={m.id} value={m.id}>
                      <span className="aiv-model-dot" style={{ background: m.dot }} />
                      <span className="aiv-model-label">{m.label}</span>
                      <span className="aiv-model-credit">⚡ {m.credits}</span>
                    </MenuItem>
                  ))}
                </Select>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>⚡ <strong>{creditsBalance.toLocaleString()}</strong> credits available</span>
                </div>
              </div>

              {/* Prompt */}
              <div className="aiv-field">
                <div className="aiv-label-row">
                  <label className="aiv-label">Prompt <span className="aiv-required">*</span></label>
                  <button className="aiv-insert-var-btn" onClick={() => setShowVars(v => !v)}>
                    {'{{ Insert Variable }}'}
                  </button>
                </div>
                <div style={{ position: 'relative' }} ref={promptRef}>
                  {promptFocused ? (
                    <textarea
                      ref={promptEditRef}
                      value={prompt}
                      onChange={handlePromptChange}
                      onKeyDown={handlePromptKeyDown}
                      onBlur={() => setPromptFocused(false)}
                      autoFocus
                      rows={6}
                      placeholder="Write a unique, 1-sentence icebreaker for {{first_name}} at {{company_name}}..."
                      className="aiv-prompt-raw-textarea"
                    />
                  ) : (
                    <div onClick={() => setPromptFocused(true)} className="aiv-prompt-chip-display" dangerouslySetInnerHTML={{ __html: prompt ? renderPromptAsHtml(prompt) : '<span class="aiv-prompt-placeholder">Write a unique, 1-sentence icebreaker for <span class="aiv-prompt-chip">{{first_name}}</span> at <span class="aiv-prompt-chip">{{company_name}}</span>...</span>' }} />
                  )}
                  {varDropdown.show && filteredVarDropdown.length > 0 && (
                    <div className="variable-dropdown" style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 9999, maxHeight: 200, overflowY: 'auto' }}>
                      <div className="variable-dropdown-section-label">VARIABLES</div>
                      {filteredVarDropdown.map((v, i) => (
                        <div
                          key={v}
                          className={`variable-item${i === varDropdownIndex ? ' variable-item:hover' : ''}`}
                          style={i === varDropdownIndex ? { background: '#F5F3FF', color: 'var(--primary-color)' } : {}}
                          onMouseDown={(e) => { e.preventDefault(); insertVarFromDropdown(v); }}
                        >
                          {v}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {showVars && (
                  <div className="aiv-vars-row">
                    <div className="aiv-vars-label">AVAILABLE VARIABLES</div>
                    <div className="aiv-vars-chips">
                      {dynamicVariables.map(v => (
                        <button key={v} className="aiv-var-chip" onClick={() => insertVariable(v)}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Preview For */}
              {previewContacts.length > 0 && (
                <div className="aiv-field">
                  <label className="aiv-label">Preview For</label>
                  <div className="aiv-avatar-row">
                    {previewContacts.map((c, i) => {
                      const isSelected = previewContact?._id === c._id;
                      const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || '?';
                      const shortName = c.firstName || name.split(' ')[0] || '?';
                      return (
                        <button
                          key={c._id}
                          className={`aiv-avatar-chip${isSelected ? ' selected' : ''}`}
                          style={{ '--chip-color': AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                          onClick={() => setPreviewContact(c)}
                        >
                          <span className="aiv-avatar-chip-dot" style={{ background: isSelected ? '#fff' : AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                            {getInitials(c)}
                          </span>
                          <span className="aiv-avatar-chip-name">{shortName}</span>
                        </button>
                      );
                    })}
                  </div>

                  {previewContact && (
                    <div className="aiv-preview-box">
                      <div className="aiv-preview-label">
                        PREVIEW — {`${previewContact.firstName || ''} ${previewContact.lastName || ''}`.trim().toUpperCase() || previewContact.email?.toUpperCase()}
                      </div>
                      <div className="aiv-preview-text">
                        {aiVariablesLoading
                          ? <span className="aiv-preview-loading"><CircularProgress size={14} /> Generating...</span>
                          : previewText || <span className="aiv-preview-empty">Click "Test Preview" to see a sample output</span>
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Credit Estimate */}
              <div className="aiv-credit-estimate">
                {usingOwnKey ? (
                  <> 🔑  <strong>Using your own API key</strong> — no credits charged for {totalContacts} contacts</>
                ) : (
                  <>⚡ <strong>Credit estimate:</strong> {creditEstimate} credits for {totalContacts} contacts</>
                )}
              </div>
            </div>
          )}

          {/* ── Tab 1: Templates ── */}
          {tab === 1 && (
            <div className="aiv-templates">
              <div className="aiv-search-box">
                <SearchIcon style={{ fontSize: 16, color: '#9CA3AF' }} />
                <input
                  className="aiv-search-input"
                  placeholder="Search templates..."
                  value={templateSearch}
                  onChange={e => setTemplateSearch(e.target.value)}
                />
              </div>
              <div className="aiv-template-list">
                {filteredTemplates.map((t, i) => (
                  <div key={i} className="aiv-template-card">
                    <div className="aiv-template-top">
                      <div className="aiv-template-info">
                        <div className="aiv-template-title">{t.title}</div>
                        <div className="aiv-template-desc">{t.description}</div>
                        <div className="aiv-template-meta">
                          <span className="aiv-template-runs">🔥 {t.runs} runs</span>
                          <span className="aiv-template-cat" style={{ background: CATEGORY_COLORS[t.category]?.bg, color: CATEGORY_COLORS[t.category]?.color }}>{t.category}</span>
                        </div>
                      </div>
                      <div className="aiv-template-right">
                        <span className="aiv-template-credit">⚡ {t.credits}</span>
                        <Button size="small" variant="contained" className="aiv-use-btn" onClick={() => handleUseTemplate(t)}>Use →</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Tab 2: Settings ── */}
          {tab === 2 && (
            <div className="aiv-settings">
              <div className="aiv-settings-banner">
                ℹ Connect your own API keys to bypass SellAgent credits entirely. Leave blank to use platform credits automatically.
              </div>
              {[
                { key: 'openai',    label: 'OpenAI',           placeholder: 'sk-', color: '#10B981' },
                { key: 'anthropic', label: 'Anthropic (Claude)', placeholder: 'sk-ant-', color: '#8B5CF6' },
              ].map(({ key, label, placeholder, color }) => (
                <div key={key} className="aiv-key-field">
                  <div className="aiv-key-label-row">
                    <span className="aiv-key-dot" style={{ background: color }} />
                    <label className="aiv-key-label">{label}</label>
                    {aiVariableKeyStatus[key] && <span className="aiv-key-saved">Key saved ✓</span>}
                  </div>
                  <TextField
                    fullWidth size="small" type="password"
                    value={apiKeys[key]}
                    onChange={e => setApiKeys(k => ({ ...k, [key]: e.target.value }))}
                    placeholder={aiVariableKeyStatus[key] ? '••••••••••••••••' : placeholder}
                  />
                </div>
              ))}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="aiv-footer">
          {tab === 0 && (
            <>
              <Button variant="outlined" size="small" className="aiv-test-btn" onClick={handleTestPreview} disabled={!prompt || aiVariablesLoading}>
                {aiVariablesLoading ? <CircularProgress size={14} style={{ marginRight: 6 }} /> : null}
                ▶ Test Preview
              </Button>
              <div className="aiv-run-btn-wrap">
                <Button
                  variant="contained" size="small"
                  className="aiv-run-btn"
                  onClick={() => handleRunN('all')}
                  disabled={!prompt || aiVariablesLoading}
                >
                  {aiVariablesLoading ? <CircularProgress size={14} style={{ marginRight: 6, color: '#fff' }} /> : null}
                  ✦ Run All ({totalContacts} rows)
                </Button>
                <Button
                  variant="contained" size="small"
                  className="aiv-run-arrow-btn"
                  onClick={() => setShowRunMenu(v => !v)}
                  disabled={aiVariablesLoading}
                >
                  <ArrowDownIcon style={{ fontSize: 16 }} />
                </Button>
                {showRunMenu && (
                  <div className="aiv-run-menu" style={{ position: 'absolute', bottom: '110%', right: 0, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 260, zIndex: 999, overflow: 'hidden' }}>
                    {/* Option 1: Enrich first N */}
                    <div className="aiv-run-menu-item" style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap' }}>Enrich first</span>
                      <input
                        type="number" min={1} max={totalContacts}
                        value={enrichFirst}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setEnrichFirst(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{ width: 52, border: '1px solid #E5E7EB', borderRadius: 6, padding: '3px 6px', fontSize: 13, textAlign: 'center', outline: 'none' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>leads</span>
                      <span
                        onClick={() => handleRunN(enrichFirst)}
                        style={{ marginLeft: 'auto', background: '#F3F4F6', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        ~{enrichFirst} total
                      </span>
                    </div>
                    {/* Option 2: Enrich 100 */}
                    <div
                      className="aiv-run-menu-item"
                      onClick={() => handleRunN(100)}
                      style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>Enrich 100 leads</span>
                      <span style={{ background: '#F3F4F6', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: '#374151' }}>~100 total</span>
                    </div>
                    {/* Option 3: Enrich all */}
                    <div
                      className="aiv-run-menu-item"
                      onClick={() => handleRunN('all')}
                      style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>Enrich all leads in the list</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {tab === 2 && (
            <Button variant="contained" fullWidth className="aiv-save-keys-btn" onClick={handleSaveKeys} disabled={savingKeys} startIcon={<SaveIcon />}>
              {savingKeys ? 'Saving...' : 'Save API Keys'}
            </Button>
          )}
        </div>

      </div>
    </Drawer>
  );
};

export default AIVariablesDrawer;
